import { Button, Modal } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "../../styles/shared/RequestLiveSuggestionModal.module.css";
import { FaBrain } from "react-icons/fa";
import SuggestionTabs from "./SuggestionTabs";
import AnalysisInfo from "./AnalysisInfo";
import BacktestChart from "./BacktestChart";
import BacktestHistory from "./BacktestHistory";
import SuggestionConfig from "./SuggestionConfig";
import { IoClose } from "react-icons/io5";
import {
  getLiveSuggestion,
  LIVE_TIMEFRAMES,
} from "../../redux/live-suggestion/liveSuggestionSlice";
import useSuggestedSignal from "../../hooks/useSuggestedSignal";
import { LazyLoadImage } from "react-lazy-load-image-component";

function initialBacktestTimeframe(value) {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  return LIVE_TIMEFRAMES.find((frame) => frame.toLowerCase() === raw) || "1h";
}

function RequestLiveSuggestionModal({ show, setShow, coin, exchange }) {
  const {
    liveSuggestion: { coinStatus },
    main: { userData },
    tradingAssistant: { selectedTimeFrame },
  } = useSelector((state) => state);

  const [selectedExchange, setSelectedExchange] = useState(
    exchange || "binance",
  );
  const [selectedCoin, setSelectedCoin] = useState(coin || "BTC");
  const [selectedTimeframe, setSelectedTimeframe] = useState(() =>
    initialBacktestTimeframe(selectedTimeFrame),
  );
  const [activeTab, setActiveTab] = useState("analysis");
  const [requestPending, setRequestPending] = useState(false);

  const { liveSuggestion, chartData } = useSuggestedSignal({
    coin: selectedCoin,
    timeframe: selectedTimeframe,
    oppositeSignal: "",
  });

  const controllersRef = useRef(new Set());
  const requestSeq = useRef(0);
  const dispatch = useDispatch();

  const coinLoading = coinStatus?.[selectedCoin] === "loading";
  const signalLoading = requestPending || coinLoading;

  const requestSuggestion = useCallback(
    (nextExchange, nextCoin, nextTimeframe) => {
      if (
        !nextExchange ||
        !nextCoin ||
        !LIVE_TIMEFRAMES.includes(nextTimeframe)
      ) {
        return;
      }

      const seq = ++requestSeq.current;
      const controller = new AbortController();
      controllersRef.current.add(controller);
      setRequestPending(true);

      const pending = dispatch(
        getLiveSuggestion(
          userData?.["custom:custom_username"],
          userData?.["cognito:username"],
          nextExchange,
          nextCoin,
          true,
          controller.signal,
          true,
          true,
          0,
          [],
          nextTimeframe,
        ),
      );

      Promise.resolve(pending).finally(() => {
        controllersRef.current.delete(controller);
        if (requestSeq.current === seq) {
          setRequestPending(false);
        }
      });
    },
    [dispatch, userData],
  );

  const cancelSuggestion = useCallback(() => {
    requestSeq.current += 1;
    controllersRef.current.forEach((controller) => controller.abort());
    controllersRef.current.clear();
    setRequestPending(false);
  }, []);

  useEffect(() => {
    const controllers = controllersRef.current;
    return () => {
      requestSeq.current += 1;
      controllers.forEach((controller) => controller.abort());
      controllers.clear();
    };
  }, []);

  const contentTitle =
    activeTab === "analysis"
      ? "Current AI Signal"
      : activeTab === "chart"
        ? "Backtest Performance Chart"
        : "Backtest Trade History";

  const footerText =
    activeTab === "analysis"
      ? "Note: This suggestion is for the selected exchange, pair, and timeframe."
      : activeTab === "chart"
        ? "AI-driven portfolio simulation over the last 3-4 days."
        : "List of AI-based simulated trades during backtesting.";

  const closeModal = () => {
    cancelSuggestion();
    setShow(false);
    setSelectedExchange(coin || "BTC");
    setSelectedExchange(exchange || "binance");
  };

  const refreshSuggestion = () => {
    if (signalLoading) return;
    requestSuggestion(selectedExchange, selectedCoin, selectedTimeframe);
  };

  const changeTab = (tab) => {
    setActiveTab(tab);
    if (tab !== "analysis" && !chartData && !signalLoading) {
      requestSuggestion(selectedExchange, selectedCoin, selectedTimeframe);
    }
  };

  return (
    <Modal
      open={show}
      maskClosable={false}
      footer={null}
      centered
      onCancel={closeModal}
      className={styles.container}
      width={700}
    >
      <div className={styles.header}>
        {selectedCoin && (
          <h6 className={styles.title}>
            <LazyLoadImage
              src={`https://cryptocrispypro.s3.ap-southeast-2.amazonaws.com/symbol_icons/${selectedCoin}.png`}
              alt={selectedCoin}
              width="23px"
              height="23px"
              effect="blur"
              style={{ borderRadius: "50%" }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/placeholders/asset-logo.svg";
              }}
            />
            <span>{selectedCoin}/USDT</span>
          </h6>
        )}
        <div className={styles.handlerBtns}>
          <Button
            className={styles.submitBtn}
            loading={signalLoading}
            onClick={refreshSuggestion}
          >
            {liveSuggestion ? "Refresh" : "Run AI Backtest"}
            {!signalLoading && <FaBrain />}
          </Button>

          <IoClose size={25} onClick={closeModal} className={styles.closeBtn} />
        </div>
      </div>
      <div className={styles.body}>
        <SuggestionConfig
          selectedExchange={selectedExchange}
          setSelectedExchange={setSelectedExchange}
          selectedCoin={selectedCoin}
          setSelectedCoin={setSelectedCoin}
          selectedTimeframe={selectedTimeframe}
          setSelectedTimeframe={setSelectedTimeframe}
          signalLoading={signalLoading}
          requestSuggestion={requestSuggestion}
          cancelSuggestion={cancelSuggestion}
        />

        <SuggestionTabs activeTab={activeTab} setActiveTab={changeTab} />

        <div className={styles.content}>
          <h4 className={styles.contentTitle}>{contentTitle}</h4>
          {activeTab === "analysis" && (
            <AnalysisInfo
              coin={selectedCoin}
              timeframe={selectedTimeframe}
              loading={signalLoading}
            />
          )}
          {activeTab === "chart" && (
            <BacktestChart
              coin={selectedCoin}
              exchange={selectedExchange}
              timeframe={selectedTimeframe}
              loading={signalLoading}
            />
          )}
          {activeTab === "history" && (
            <BacktestHistory
              coin={selectedCoin}
              timeframe={selectedTimeframe}
              loading={signalLoading}
            />
          )}
        </div>

        <div className={styles.note}>
          <span className={styles.highlight}></span>
          <p>{footerText}</p>
        </div>
      </div>
    </Modal>
  );
}

export default RequestLiveSuggestionModal;
